import { http } from '../http'
import type {
	CancelSubscriptionResponse,
	CheckoutResponse,
	OrderResponse,
	OrderStatus,
	Plan,
	PlanResource,
	WireCheckoutResponse,
	WireOrderResponse,
	WireOrderStatus,
	WirePlan
} from '../types'
import {
	mockAbortPayment,
	mockCancelSubscription,
	mockGetOrder,
	mockGetPlans,
	mockPostCheckout
} from '@/mocks/billingMock'
import { PRO_PLAN_FALLBACK } from '@/constants/pro'

const ORDER_STATUS: Record<WireOrderStatus, OrderStatus> = {
	pending: 'pending',
	success: 'succeeded',
	fail: 'failed'
}

/**
 * Переводит статус заказа в наш. Незнакомое значение считаем незавершённым: поллер
 * продолжит спрашивать и остановится сам по дедлайну. Объявить терминальным исход,
 * которого мы не понимаем, хуже — это либо потерянный успех, либо ложная ошибка
 * на экране человека, с которого уже списали деньги.
 */
function toOrderStatus(raw: string | undefined): OrderStatus {
	const known = raw ? ORDER_STATUS[raw as WireOrderStatus] : undefined
	if (known) return known
	console.warn('[billing] неизвестный статус заказа', raw)
	return 'pending'
}

/**
 * Тарифы: цена, срок и лимиты, которые фронт показывает вместо захардкоженных чисел.
 * Идентификатор тарифа для чекаута фронт тоже берёт отсюда, поэтому ручка спрашивается
 * на старте приложения (см. stores/plans.ts).
 *
 * Негодные элементы массива выбрасываем, а не достраиваем: пустой результат уводит стор
 * на фолбэк с прежними числами, и это честнее наполовину выдуманного тарифа.
 */
export async function getPlans(): Promise<Plan[]> {
	// MOCK
	if (import.meta.env.VITE_AUTH_MOCK) {
		return toPlans(await mockGetPlans())
	}
	return http.get('/billing/plans').then((response) => toPlans(unwrap<WirePlan[]>(response.data)))
}

export async function postCheckout(planId: string): Promise<CheckoutResponse> {
	// MOCK
	if (import.meta.env.VITE_AUTH_MOCK) {
		return toCheckout(await mockPostCheckout(planId))
	}
	// Конверт снимает сам toCheckout: у payload виджета есть собственное поле data,
	// и общий unwrap вернул бы его вместо тела ответа.
	return http.post('/billing/checkout', { plan_id: planId }).then((response) => toCheckout(response.data))
}

/**
 * Статус заказа. options нужен одному вызову — проверке перед отменой оплаты: у неё
 * свой таймаут, потому что она держит кнопку оплаты (см. billing.isOrderPaid).
 * Поллеру таймаут не нужен: его останавливает собственный дедлайн.
 */
export async function getOrder(orderId: string, options: { timeout?: number } = {}): Promise<OrderResponse> {
	// MOCK
	if (import.meta.env.VITE_AUTH_MOCK) {
		return toOrder(await mockGetOrder(orderId))
	}
	return http
		.get(`/billing/orders/${orderId}`, options)
		.then((response) => toOrder(unwrap<WireOrderResponse>(response.data)))
}

/**
 * Свой таймаут: у axios-инстанса глобального нет, а этот запрос держит кнопку оплаты
 * (см. billing.cancel — она ждёт ответа, чтобы abort не прилетел поверх нового заказа).
 * Зависшая ручка иначе заперла бы человека без права начать оплату заново.
 */
const ABORT_TIMEOUT_MS = 7000

/**
 * Отмена незавершённой оплаты: человек закрыл платёжный виджет крестиком, не начав
 * списание. Тело пустое — какой заказ гасить, решает сам бэк.
 */
export async function postAbortPayment(): Promise<void> {
	// MOCK
	if (import.meta.env.VITE_AUTH_MOCK) {
		return mockAbortPayment()
	}
	await http.post('/billing/abort-payment', undefined, { timeout: ABORT_TIMEOUT_MS })
}

/** Отключение автопродления. Тело пустое, вид подписки определяет сервер. */
export async function postCancelSubscription(): Promise<CancelSubscriptionResponse> {
	// MOCK
	if (import.meta.env.VITE_AUTH_MOCK) {
		return mockCancelSubscription()
	}
	return http.post('/billing/subscription/cancel').then((response) => unwrap<CancelSubscriptionResponse>(response.data))
}

function toPlans(wire: WirePlan[] | unknown): Plan[] {
	if (!Array.isArray(wire)) {
		console.warn('[billing] /billing/plans ответил не массивом', wire)
		return []
	}
	return wire.map(toPlan).filter((plan): plan is Plan => plan !== null)
}

/**
 * Единственное место, где копейки превращаются в рубли.
 *
 * Путаницу единиц (399 вместо 39900) здесь распознать нельзя: 399 копеек — законная цена
 * в 3,99 ₽, и отличить её от рублей, присланных по ошибке, неоткуда. Ловит эту ошибку
 * ассерт суммы в usePaymentFlow: он сверяет payload виджета (в рублях) с ценой тарифа
 * и не открывает виджет при расхождении — то есть до того, как в игру войдут деньги.
 *
 * Здесь отбрасываем только то, с чем работать нечем: тариф без слага (его нечем оплатить)
 * и без цены (её нечем показать). Недостающие currency/days_period/limits берём из фолбэка:
 * контракт бэк выкатывает по частям, и одно пустое поле не повод остаться без тарифа.
 */
function toPlan(wire: WirePlan): Plan | null {
	const slug = wire.slug?.trim()
	if (!slug) {
		console.warn('[billing] тариф без слага пропущен', wire)
		return null
	}

	// Ноль — законная цена бесплатного тарифа, поэтому проверяем на отрицательное, а не на falsy.
	const price = wire.price
	if (typeof price !== 'number' || !Number.isFinite(price) || price < 0) {
		console.warn('[billing] тариф с негодной ценой пропущен', slug, price)
		return null
	}

	return {
		// Слаг как запасное название: подставить сюда «PomogAI Pro» значило бы так назвать
		// и бесплатный тариф, если бэк однажды пришлёт его без name.
		name: wire.name?.trim() || slug,
		slug,
		priceMinor: price,
		priceMajor: price / 100,
		daysPeriod: positive(wire.days_period) ?? PRO_PLAN_FALLBACK.daysPeriod,
		currency: wire.currency?.trim() || PRO_PLAN_FALLBACK.currency,
		limits: {
			text: limit(wire, 'text'),
			image: limit(wire, 'image'),
			song: limit(wire, 'song')
		}
	}
}

function positive(value: unknown): number | null {
	return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

/** Лимит ресурса. Ноль — законное значение (тариф без песен), поэтому 0 не фолбэчим. */
function limit(wire: WirePlan, resource: PlanResource): number {
	const value = wire.limits?.[resource]
	if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value
	return PRO_PLAN_FALLBACK.limits[resource]
}

/**
 * Сводит обе формы ответа чекаута к одной (см. WireCheckoutResponse).
 *
 * Единственное, что фронту нужно от чекаута, — payload виджета: номер заказа он берёт
 * из него же (invoiceId), а статуса у только что созданного заказа быть не может.
 */
function toCheckout(body: unknown): CheckoutResponse {
	const wire = unwrapCheckout(body)
	return {
		// Статуса в новой форме нет, и это не повод предупреждать: заказ только создан.
		status: wire.status === undefined ? 'pending' : toOrderStatus(wire.status),
		expires_at: wire.expires_at ?? null,
		// Payload либо лежит в widget (старая форма), либо им является всё тело (новая).
		widget: isObject(wire.widget) ? wire.widget : wire
	}
}

function isObject(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object'
}

/**
 * Признаки тела чекаута — по ним отличаем его от конверта. Полей два поколения виджета
 * (publicId/publicTerminalId) плюс старая обёртка; хватает любого.
 */
const CHECKOUT_MARKERS = ['invoiceId', 'widget', 'order_id', 'publicId', 'publicTerminalId']

function looksLikeCheckout(value: unknown): boolean {
	return isObject(value) && CHECKOUT_MARKERS.some((marker) => marker in value)
}

/**
 * Снимает необязательный конверт data — но только убедившись, что внутри тело чекаута.
 * Слепое разворачивание здесь опасно: у самого payload есть поле data
 * ({ intention, user_id }), и общий unwrap вернул бы его вместо заказа.
 */
function unwrapCheckout(body: unknown): WireCheckoutResponse {
	if (looksLikeCheckout(body)) return body as WireCheckoutResponse
	const inner = isObject(body) ? body.data : undefined
	if (looksLikeCheckout(inner)) return inner as WireCheckoutResponse
	// Ни то ни другое: отдаём как есть, дальше отсутствие invoiceId остановит оплату.
	return (isObject(body) ? body : {}) as WireCheckoutResponse
}

function toOrder(wire: WireOrderResponse): OrderResponse {
	return {
		order_id: wire.order_id,
		status: toOrderStatus(wire.status),
		failure_reason: wire.failure_reason ?? null,
		expires_at: wire.expires_at ?? null
	}
}

/**
 * Конверт data необязателен: остальные ручки проекта отдают то с ним, то без него
 * (ср. normalizeUsage в stores/usage.ts), и биллинг не повод чинить это глобально.
 */
function unwrap<T>(body: unknown): T {
	if (body && typeof body === 'object' && 'data' in body) {
		const inner = (body as { data?: unknown }).data
		if (inner && typeof inner === 'object') return inner as T
	}
	return body as T
}
